import { all, put, takeEvery } from 'redux-saga/effects';
import { userService } from 'services/api';
import {
  CustomizableSong,
  FreeSong,
  HTTPError,
  PaginatedList,
  PrivateUser,
  UserBasicData,
} from 'services/api/entities';
import { logoutSuccess } from '../auth/actions';
import { errorAction, startAction, stopAction } from '../base/actions';
import { fetchAndCombineSongsData } from '../helpers/fetchAndCombineSongsData';
import { actions } from '../newSongs';
import { Song } from '../songs/types';
import { Action } from '../types';
import { songsLibrarySuccess, userReset, userSuccess } from './actions';
import userActions from './actions/constants';

/**
 * Request to update user data
 * @param action
 */
export function* asyncUpdateUser(
  action: Action<{
    id: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
      password?: string;
    };
  }>
) {
  const { id, user } = action.payload;
  const userData = new UserBasicData({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    password: user.password,
  });
  yield put(startAction(action));
  try {
    const updatedUser: PrivateUser = yield userService.update(id, userData);
    yield put(userSuccess(updatedUser));
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Request to fetch user object from backend
 * @param action
 */
export function* asyncFetchUser(action: Action<void>) {
  yield put(startAction(action));
  try {
    const user: PrivateUser = yield userService.getSessionUser();
    yield put(userSuccess(user));
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Request to delete user
 * @param action
 */
export function* asyncDeleteUser(action: Action<{ id: string }>) {
  yield put(startAction(action));
  try {
    yield userService.deleteAccount();
    yield put(logoutSuccess());
    yield put(userReset());
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Request to get list of user songs library
 * @param action
 */
export function* asyncSongLibrary(
  action: Action<{ page: number; unifiedLibraryStructure: boolean }>
) {
  const { page, unifiedLibraryStructure } = action.payload;

  yield put(startAction(action));

  try {
    const songList: PaginatedList<FreeSong | CustomizableSong> =
      yield userService.getSongsLibrary({
        page,
        unified_library_structure: unifiedLibraryStructure,
      });
    const songs = songList.items;

    const songsObj: Record<string, Song> =
      yield fetchAndCombineSongsData(songs);

    const { storeSongs } = actions;
    yield put(storeSongs({ songs: songsObj }));
    yield put(
      songsLibrarySuccess({
        songs: songsObj,
        paginationData: songList.paginationData,
      })
    );
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/* ----------------------------------------------------------------------------------------
 * Watch sagas
 * ----------------------------------------------------------------------------------------*/

function* watchUpdateUser() {
  yield takeEvery(userActions.USER_UPDATE, asyncUpdateUser);
}

function* watchFetchUser() {
  yield takeEvery(userActions.USER_FETCH, asyncFetchUser);
}

function* watchDeleteUser() {
  yield takeEvery(userActions.USER_DELETE, asyncDeleteUser);
}

function* watchSongsLibrary() {
  yield takeEvery(userActions.USER_LIBRARY, asyncSongLibrary);
}

export default function* rootSaga() {
  yield all([
    watchUpdateUser(),
    watchFetchUser(),
    watchDeleteUser(),
    watchSongsLibrary(),
  ]);
}
